class SearchForm extends HTMLElement {
  static categoryForms = new WeakSet();
  static submissionStates = new WeakMap();

  static categoryRules = [
    ['chandeliers?', '120203509989'],
    ['lighting', ['120203509989', '120203641061', '120203608293']],
    ['vases?', '120202723557'],
    ['bowls?|vessels?', '120198922469'],
    ['box(?:es)?', '120196858085'],
    ['chairs?', '120208785637'],
    ['mirrors?', '120203346149'],
    ['sculptures?', '120203149541'],
    ['sofas?', '120211046629'],
    ['coffee\\s+tables?', '120203706597'],
    ['console\\s+tables?', '120203935973'],
    ['dining\\s+tables?', '120204230885'],
    ['floor\\s+lamps?', '120203608293'],
    ['table\\s+lamps?', '120203641061'],
    ['side\\s+tables?', '120204263653'],
    ['nesting\\s+tables?', '156829188325'],
    ['nightstands?', '121665683685'],
    ['ottomans?|stools?', '120209113317'],
    ['benches|bench', '120208752869'],
    ['buffets?', '120208687333'],
    ['chests?\\s+of\\s+drawers', '120208720101'],
    ['game\\s+tables?', '121665192165'],
    ['picture\\s+frames?', '120201183461'],
    ['pillows?', '120201412837'],
    ['trays?', '120201478373'],
    ['candle\\s+holders?|candles?', '120200855781'],
    ['paintings?', '120203018469'],
    ['book\\s+ends?', '121660211429'],
    ['pedestals?', '121661685989'],
    ['risers?', '121212338405'],
    ['bar\\s+ware', '120195940581'],
  ].map(([pattern, ids]) => ({
    pattern: new RegExp(`(?:^|[\\s,!?])(?:${pattern})(?=\\s|$|[,.!?])`, 'i'),
    values: (Array.isArray(ids) ? ids : [ids]).map((id) => `gid://shopify/Metaobject/${id}`),
  }));

  static getWordVariants(word) {
    const variants = [word];
    if (!/^[a-z]+$/i.test(word) || word.length < 4 || /(?:ss|us|is|ves)$/i.test(word)) return variants;
    if (/ies$/i.test(word) && word.length > 4) {
      variants.push(`${word.slice(0, -3)}y`);
    } else if (/(?:ches|shes|sses|xes|zes|uses)$/i.test(word)) {
      variants.push(word.slice(0, -2));
    } else if (/s$/i.test(word)) {
      variants.push(word.slice(0, -1));
    }
    return variants;
  }

  static getSearchRequest(query, { preserveCategoryTerms = false } = {}) {
    const original = query.trim().replace(/\s+/g, ' ');
    if (
      !original ||
      /["\u201c\u201d():*\\]/.test(original) ||
      /(?:^|\s)-\S|\b(?:AND|OR|NOT)\b/.test(original)
    ) {
      return null;
    }

    const categories = this.categoryRules.filter(({ pattern }) => pattern.test(original));
    const category = !preserveCategoryTerms && categories.length === 1 ? categories[0] : null;
    const lexical = category ? original.replace(new RegExp(category.pattern.source, 'gi'), ' ') : original;
    const words = lexical.split(/\s+/).filter((word) => word && !/^(?:in|by|and|of|with|the|a|an|for|&)$/i.test(word));
    const fields = ['title', 'body', 'vendor', 'tag', 'product_type'];
    const quote = (value) => `"${value.replace(/\\/g, '\\\\').replace(/"/g, '\\"')}"`;
    const groups = words.map((word) => `(${this.getWordVariants(word).flatMap((variant) => fields.map((field) => `${field}:${quote(variant)}`)).join(' OR ')})`);
    const strict = groups.length ? groups.join(' AND ') : 'title:*';
    return {
      query: `(title:${quote(original)} OR (${strict}))`,
      filters: category ? category.values : [],
    };
  }

  static getManualCategoryFilters(form) {
    return Array.from(form.querySelectorAll('[name="filter.p.m.custom.categories"]'))
      .filter((input) => !input.hasAttribute('data-search-category-filter'))
      .map((input) => input.value)
      .filter(Boolean);
  }

  static restoreSearchFields(form) {
    const state = this.submissionStates.get(form);
    if (state?.cleanupTimer) clearTimeout(state.cleanupTimer);
    this.submissionStates.delete(form);
    form.querySelectorAll('[data-search-precision-query], [data-search-category-filter]').forEach((input) => input.remove());
    form.querySelectorAll('[data-search-precision-original-name]').forEach((input) => {
      input.name = input.getAttribute('data-search-precision-original-name');
      input.removeAttribute('data-search-precision-original-name');
    });
    form.querySelectorAll('[data-search-original-type]').forEach((input) => {
      input.value = input.getAttribute('data-search-original-type');
      input.removeAttribute('data-search-original-type');
    });
  }

  static onSearchSubmit(event, form) {
    this.restoreSearchFields(form);
    const input = form.querySelector('input[type="search"][name="q"]');
    if (!input?.value.trim() || form.querySelector('[aria-selected="true"] a')) return;
    const manualCategories = this.getManualCategoryFilters(form);
    const request = this.getSearchRequest(input.value, { preserveCategoryTerms: manualCategories.length > 0 });
    if (!request) return;

    input.setAttribute('data-search-precision-original-name', input.name);
    input.removeAttribute('name');
    const existingType = form.querySelector('[name="type"]');
    if (existingType) {
      existingType.setAttribute('data-search-original-type', existingType.value);
      existingType.value = 'product';
    }
    const entries = [['q', request.query]];
    if (!existingType) entries.push(['type', 'product']);
    request.filters.forEach((value) => entries.push(['filter.p.m.custom.categories', value]));
    for (const [name, value] of entries) {
      const hidden = form.ownerDocument.createElement('input');
      hidden.type = 'hidden';
      hidden.name = name;
      hidden.value = value;
      hidden.setAttribute(name === 'filter.p.m.custom.categories' ? 'data-search-category-filter' : 'data-search-precision-query', '');
      form.appendChild(hidden);
    }

    const state = { cleanupTimer: setTimeout(() => this.restoreSearchFields(form), 0) };
    this.submissionStates.set(form, state);
  }

  constructor() {
    super();
    this.input = this.querySelector('input[type="search"]');
    this.resetButton = this.querySelector('button[type="reset"]');

    if (this.input) {
      this.input.form.addEventListener('reset', this.onFormReset.bind(this));
      this.input.addEventListener(
        'input',
        debounce((event) => {
          this.onChange(event);
        }, 300).bind(this)
      );
      if (!SearchForm.categoryForms.has(this.input.form)) {
        this.input.form.addEventListener('submit', (event) => SearchForm.onSearchSubmit(event, this.input.form));
        this.input.addEventListener('input', () => SearchForm.restoreSearchFields(this.input.form));
        this.input.form.addEventListener('reset', () => SearchForm.restoreSearchFields(this.input.form));
        window.addEventListener('pageshow', () => SearchForm.restoreSearchFields(this.input.form));
        SearchForm.categoryForms.add(this.input.form);
      }
    }
  }

  toggleResetButton() {
    const resetIsHidden = this.resetButton.classList.contains('hidden');
    if (this.input.value.length > 0 && resetIsHidden) {
      this.resetButton.classList.remove('hidden');
    } else if (this.input.value.length === 0 && !resetIsHidden) {
      this.resetButton.classList.add('hidden');
    }
  }

  onChange() {
    this.toggleResetButton();
  }

  shouldResetForm() {
    return !document.querySelector('[aria-selected="true"] a');
  }

  onFormReset(event) {
    // Prevent default so the form reset doesn't set the value gotten from the url on page load
    event.preventDefault();
    // Don't reset if the user has selected an element on the predictive search dropdown
    if (this.shouldResetForm()) {
      this.input.value = '';
      this.input.focus();
      this.toggleResetButton();
    }
  }
}

customElements.define('search-form', SearchForm);

